import type { Interceptor } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';

import { config } from '@/config/config';
import { getSessionToken } from '@/domains/authentication/session';
import { isMockMode } from '@/lib/mockMode';

import i18n from '../i18n/i18n';

const delayInterceptor = (delayMs: number): Interceptor => {
	return (next) => async (req) => {
		await new Promise((resolve) => setTimeout(resolve, delayMs));
		return next(req);
	};
};

const addLanguageHeaderInterceptor: Interceptor = (next) => async (req) => {
	const language = i18n.language;
	req.header.append('Accept-Language', language);
	return await next(req);
};

const addMockModeHeaderInterceptor: Interceptor = (next) => async (req) => {
	if (isMockMode()) {
		req.header.set('X-Mock-Mode', 'true');
	}
	return next(req);
};

const addSessionInterceptor: Interceptor = (next) => async (req) => {
	const token = getSessionToken();
	if (token) {
		req.header.set('Authorization', `Bearer ${token}`);
	}
	return next(req);
};

export const getTransport = () => {
	const interceptors: Interceptor[] = [
		addSessionInterceptor,
		addLanguageHeaderInterceptor,
		addMockModeHeaderInterceptor,
	];

	if (import.meta.env.DEV) {
		interceptors.push(delayInterceptor(400));
	}

	return createConnectTransport({
		baseUrl: config.apiUrl,
		interceptors,
		fetch: (url, options) => {
			return fetch(url, {
				...options,
				credentials: 'include',
			});
		},
	});
};
